import { useRef } from 'preact/hooks';
import type { Content, UnlockableDef } from '../../../data/schema';
import type { Meta } from '../../../net/progress';
import { Padlock } from '../Padlock';
import { buyUnlockable, MAX_ACTIVE_MODIFIERS, toggleModifier } from '../unlocksLogic';
import { useEscape } from '../useEscape';
import { useInitialFocus } from '../useInitialFocus';

/** `onChange(apply)` : `apply` est appliquée à la méta du store (source de vérité), `null` si l'action n'y est plus possible. */
type OnChange = (apply: (meta: Meta) => Meta | null) => void;
interface Props { meta: Meta; content: Content; onChange: OnChange; onBack: () => void }

/** Page « Débloquables » du hub : achat en Ogrines (sorts, panoplies, familiers, modificateurs) et choix des modificateurs actifs. */
export function Unlocks({ meta, content, onChange, onBack }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  useEscape(onBack);
  useInitialFocus(panel, '.unlocks-back');
  const list = Object.values(content.unlockables);
  const achievements = Object.values(content.achievements);
  const obtained = new Set(meta.achievements.map((a) => a.id));

  return (
    <div class="panel unlocks-panel" role="dialog" aria-label="Débloquables" ref={panel}>
      <div class="unlocks-head">
        <button class="unlocks-back" type="button" onClick={onBack}>Retour</button>
        <h2>Débloquables</h2>
        <span class="unlocks-ogrines">Ogrines : <b>{meta.ogrines}</b></span>
      </div>
      <ul class="unlocks-grid">
        {list.map((def) => {
          const byAchievement = achievements.find((a) => a.unlocks === def.id);
          const achName = byAchievement ? (byAchievement.hidden && !obtained.has(byAchievement.id) ? '???' : byAchievement.name) : null;
          return <Card key={def.id} def={def} meta={meta} achName={achName} onChange={onChange} />;
        })}
      </ul>
    </div>
  );
}

function Card({ def, meta, achName, onChange }: { def: UnlockableDef; meta: Meta; achName: string | null; onChange: OnChange }) {
  const owned = meta.unlocks.includes(def.id);
  const modifierId = def.target[0]!;
  const active = meta.activeModifiers.includes(modifierId);
  const full = !active && meta.activeModifiers.length >= MAX_ACTIVE_MODIFIERS;
  // l'affichage filtre les clics impossibles ; l'achat se calcule sur la méta du store
  const buy = () => { if (buyUnlockable(meta, def)) onChange((m) => buyUnlockable(m, def)); };
  const toggle = () => onChange((m) => {
    const next = toggleModifier(m, modifierId);
    return next === m ? null : next;
  });
  return (
    <li class={`unlockable ${owned ? 'is-owned' : 'is-locked'}`} data-unlockable={def.id}>
      <span class="unlock-icon">
        <img class={owned ? undefined : 'is-grey'} src={`/assets/${def.icon}.png`} alt="" />
        {!owned && <Padlock class="unlock-lock" size={20} />}
      </span>
      <div class="unlock-body">
        <b>{def.name}</b>
        <p>{def.description}</p>
        {owned
          ? <span class="unlock-price is-owned">Débloqué</span>
          : (
            <div class="unlock-buyline">
              {def.price > 0 && <button class="unlock-buy" type="button" disabled={meta.ogrines < def.price} onClick={buy}>Débloquer ({def.price} Ogrines)</button>}
              {achName && <small>ou succès : {achName}</small>}
            </div>
          )}
        {owned && def.kind === 'modifier' && (
          <label class="unlock-active"><input type="checkbox" checked={active} disabled={full} onChange={toggle} /> Actif pour la prochaine run</label>
        )}
      </div>
    </li>
  );
}
